import { CaseModel, type CaseFormData, type ICase, type NetworkCases } from '@/models/case';
import { signedApiClient } from './apiClient';
import { FormModel, type StudyFlags } from '@/models';
import { createRandomCaseLabel } from '@/utils';


class CaseService {
  private studyEndpoint: string = '';
  private investigationEndpoint: string = '';
  private caseEndpoint: string = '';
  initService(studyEndpoint: string, investigationEndpoint: string, caseEndpoint: string) {
    this.studyEndpoint = studyEndpoint;
    this.caseEndpoint = caseEndpoint;
    this.investigationEndpoint = investigationEndpoint;
  }

  async getCases(
    studyId: string,
    page: number,
    limit: number,
    searchText?: string,
    mode?: string,
    sortField?: string,
    sortOrder?: string,
    pumpFilter?: string
  ) {
    let uri = `${this.studyEndpoint}/${studyId}/cases?page=${page}&limit=${limit}`;
    if (mode) {
      uri += `&mode=${mode}`;
    }
    if (searchText != null && searchText != '') {
      uri += `&search=${encodeURIComponent(searchText)}`;
    }
    if (sortField) {
      uri += `&sortBy=${sortField}`;
      if (sortOrder) {
        uri += `&sortOrder=${sortOrder}`;
      }
    }
    if (pumpFilter) {
      uri += `&pumpFilter=${pumpFilter}`;
    }
    const apiClient = signedApiClient('GET', uri);
    const casesResponse = await apiClient.get(uri);
    const networkResponse: NetworkCases = casesResponse.data;
    return {
      cases: networkResponse.cases.map(caseItem => CaseModel.mapFromBackend(caseItem)),
      casesCount: networkResponse.casesCount
    };
  }

  async getStudyCases(studyId: string, archive?: boolean, whatFor?: 'export') {
    let uri = `${this.studyEndpoint}/${studyId}/cases`;
    if (archive) {
      uri += '?type=archive';
    } else if (whatFor === 'export') {
      uri += '?whatFor=export';
    }
    const apiClient = signedApiClient('GET', uri);
    const casesResponse = await apiClient.get(uri);
    const networkResponse: NetworkCases = casesResponse.data;
    return {
      cases: networkResponse.cases.map(caseItem => CaseModel.mapFromBackend(caseItem)),
      casesCount: networkResponse.casesCount
    };
  }

  async getCaseFormEntries(caseId: string): Promise<FormModel[]> {
    const uri = `${this.caseEndpoint}/${caseId}/form-entries`;
    const apiClient = signedApiClient('GET', uri);
    const caseResponse = await apiClient.get(uri);
    const caseData: CaseFormData = caseResponse.data;
    return caseData.forms.map(form => FormModel.mapFormWithEntries(form));
  }

  async createCase(studyId: string, studyFlags?: StudyFlags) {
    const uri = `${this.studyEndpoint}/${studyId}/case`;
    const apiClient = signedApiClient('POST', uri);
    const postObject = {
      // eslint-disable-next-line camelcase
      case_name: createRandomCaseLabel(),
      allowDuplicateName: studyFlags?.allowDuplicateCaseNames ?? false,
      createdByUser: true
    };
    return (await apiClient.post(uri, postObject)).data;
  }

  async updateCase(caseId: string, caseName: string) {
    const uri = `${this.caseEndpoint}/${caseId}`;
    const apiClient = signedApiClient('PATCH', uri);
    const patchData = {
      label: caseName
    };
    return await apiClient.patch(uri, patchData);
  }

  async getCase(caseId?: string) {
    const uri = `case/${caseId}`;
    const apiClient = signedApiClient('GET', uri);
    const casesResponse = await apiClient.get(uri);
    const networkCase: ICase = casesResponse.data;
    return CaseModel.mapFromBackend(networkCase);
  }

  async getCaseEntries(caseId?: string) {
    const uri = `case/${caseId}/entries`;
    const apiClient = signedApiClient('GET', uri);
    const casesResponse = await apiClient.get(uri);
    const networkCase: ICase = casesResponse.data;
    return CaseModel.mapFromBackend(networkCase);
  }
}

export default new CaseService();
